/**
 * Items v2 unique effects (docs/systems/ITEMS_V2.md §1.7, R5): a ตำนาน / ไร้เทียมทาน piece that changes how a move plays, not
 * how hard it hits (none adds more than about 8 % damage on its own). One of each per character: a second copy
 * equipped does nothing. The pieces carry `unique` in their ItemDef (data/bossGear.ts); systems/gearCombat.ts plays
 * the effects that the World's hooks reach today. `live: false` marks the ones waiting for a SKILLS-V2 hook (volleys,
 * air dashes): their text shows, their effect does not run yet.
 */
export type UniqueEffect =
  /** A landed ท่าทำลาย (break) shakes the ground: มึนงง `stun` s on the other foes within `reach` m. */
  | { readonly kind: 'quakeRing'; readonly reach: number; readonly stun: number }
  /** A landed blow on a foe with at least `min` เลือดไหล stacks drains them all for `perStack` of its max HP each. */
  | { readonly kind: 'bleedConsume'; readonly min: number; readonly perStack: number }
  /** The hidden weapon's throws add พิษ and ช้า; a foe at `silenceAt` พิษ stacks is ถูกปิดจุด. */
  | { readonly kind: 'needlePoison'; readonly silenceAt: number }
  /** A ท่าเสริม (buff) cast also cleanses one debuff from the caster. */
  | { readonly kind: 'buffCleanse' }
  /** The flying-sword volley fires `extra` more swords at `pct` damage for `qiMul` the กำลังภายใน. */
  | { readonly kind: 'volleyExtra'; readonly extra: number; readonly pct: number; readonly qiMul: number }
  /** A second air dash, at `staminaMul` the พลังกาย. */
  | { readonly kind: 'airDash'; readonly staminaMul: number };

export interface UniqueDef {
  readonly id: string;
  readonly name: string;
  readonly effect: UniqueEffect;
  /** Whether the effect runs today. */
  readonly live: boolean;
  readonly text: string;
}

const list: UniqueDef[] = [
  {
    id: 'u-shadow-sword',
    name: 'กระบี่ซ่อนเงา',
    effect: { kind: 'volleyExtra', extra: 2, pct: 0.4, qiMul: 1.2 },
    live: true,
    text: 'กระบี่บินยิงเพิ่ม 2 เล่ม เล่มละ 40% แต่ใช้ลมปราณเพิ่ม 20%',
  },
  {
    id: 'u-moon-sabre',
    name: 'ดาบโลหิตกลืนจันทร์',
    effect: { kind: 'bleedConsume', min: 3, perStack: 0.02 },
    live: true,
    text: 'ฟันโดนศัตรูที่เลือดไหล 3 ชั้นขึ้นไป ดูดเลือดไหลทั้งหมดเป็นความเสียหายทันที ชั้นละ 2% ของพลังชีวิตสูงสุด',
  },
  {
    id: 'u-wind-fan',
    name: 'พัดสายลมนิรนาม',
    effect: { kind: 'buffCleanse' },
    live: true,
    text: 'ใช้ท่าเสริมแล้วล้างสถานะผิดปกติออกจากตัว 1 อย่าง',
  },
  {
    id: 'u-mountain-staff',
    name: 'กระบองทลายภูผา',
    effect: { kind: 'quakeRing', reach: 2.5, stun: 0.4 },
    live: true,
    text: 'ท่าทำลายที่เข้าเป้าทำให้พื้นสะเทือน ศัตรูรอบเป้ามึนงง 0.4 วินาที',
  },
  {
    id: 'u-plum-needles',
    name: 'เข็มเงาเหมยแดง',
    effect: { kind: 'needlePoison', silenceAt: 5 },
    live: true,
    text: 'อาวุธลับติดพิษและทำให้ช้า ศัตรูที่พิษครบ 5 ชั้นถูกปิดจุด',
  },
  {
    id: 'u-cloud-boots',
    name: 'รองเท้าเมฆาเหิน',
    effect: { kind: 'airDash', staminaMul: 2 },
    live: true,
    text: 'พุ่งกลางอากาศได้อีกครั้ง ใช้พลังกายสองเท่า',
  },
];

export const UNIQUES: Readonly<Record<string, UniqueDef>> = Object.fromEntries(list.map((u) => [u.id, u]));
export const UNIQUE_LIST: readonly UniqueDef[] = list;
